import { Box, Container, Stack } from '@dtx-company/design-system/src'
import { CSSTransition } from 'react-transition-group'
import { CodePreview } from './CodePreview/CodePreview'
import { FC, useEffect, useRef } from 'react'
import { FormActionButtons } from './FormActionButtons/FormActionButtons'
import { GeneratorHeading } from './GeneratorHeading/GeneratorHeading'
import { NAV_V2_HEIGHT } from '@dtx-company/true-common/src/constants/layout'
import { ProgressBarDesktop } from './GeneratorHeading/ProgressBar/ProgressBarDesktop'
import { ProgressBarMobile } from './GeneratorHeading/ProgressBar/ProgressBarMobile'
import { ScrollableContainer } from './ScrollableContainer/ScrollableContainer'
import { TRANSITION_DURATION } from './LoggedOutCodeGenerator.constants'
import { setHideFooter } from '@dtx-company/inter-app/src/redux/slices/settings-slice'
import { setShowStaticContent } from '@dtx-company/inter-app/src/redux/slices/homePageSlice'
import { useDispatch } from 'react-redux'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'

export const LoggedOutCodeGenerator: FC = () => {
  const { isMobile } = useScreenSizeBreakpoints()
  const dispatch = useDispatch()
  const { locgService } = useGlobalServices()
  const displayFormActionButtons = useSelector(locgService, state => !state.matches('auth'))
  const displayCodePreview = isMobile ? displayFormActionButtons : true

  useEffect(() => {
    dispatch(setHideFooter(isMobile))
    dispatch(setShowStaticContent(!isMobile))

    return () => {
      dispatch(setShowStaticContent(true))
      dispatch(setHideFooter(false))
    }
  }, [isMobile, dispatch])

  const mobileGeneratorHeadingRef = useRef<HTMLDivElement>(null)
  const mobileGeneratorHeadingHeight = mobileGeneratorHeadingRef.current?.clientHeight ?? 0
  const transformDistance = `calc((100% - ${mobileGeneratorHeadingHeight}px) * -1)`
  const commonStyles = {
    position: 'relative',
    top: 0,
    left: 0,
    right: 0
  }
  const transition = `opacity ${TRANSITION_DURATION}ms, transform ${TRANSITION_DURATION}ms ease-in-out, margin-bottom ${TRANSITION_DURATION}ms ease-in-out`

  const mobileTransitionSx = {
    '.code-preview-enter': {
      ...commonStyles,
      opacity: 0,
      transform: `translateY(${transformDistance})`,
      marginBottom: transformDistance
    },
    '.code-preview-enter-active': {
      ...commonStyles,
      opacity: 1,
      transform: 'none',
      marginBottom: 0,
      transition
    },
    '.code-preview-exit': {
      ...commonStyles,
      opacity: 1,
      marginBottom: 0,
      transform: 'none'
    },
    '.code-preview-exit-active': {
      ...commonStyles,
      opacity: 0,
      transform: `translateY(${transformDistance})`,
      marginBottom: transformDistance,
      transition
    }
  }

  return (
    <Container maxWidth="xl" disableGutters>
      <Stack
        sx={theme => ({
          height: `calc(100vh - ${NAV_V2_HEIGHT}px) `,
          width: '100%',
          borderBottom: {
            md: `1px solid ${theme.palette.border.outline}`,
            lg: 'unset'
          },
          maxHeight: {
            md: theme.spacing(209)
          },
          minHeight: {
            md: theme.spacing(150)
          }
        })}
      >
        {/* Heading is always shown on desktop */}
        <Box
          sx={{
            display: {
              xs: 'none',
              md: 'block'
            }
          }}
        >
          <GeneratorHeading />
        </Box>

        <ProgressBarDesktop />

        <Stack
          direction={{
            xs: 'column',
            md: 'row'
          }}
          sx={{
            width: '100%',
            flex: 1,
            overflow: 'hidden',
            ...mobileTransitionSx
          }}
        >
          <CSSTransition
            in={displayCodePreview}
            timeout={TRANSITION_DURATION}
            classNames="code-preview"
            unmountOnExit
            component={Box}
          >
            <>
              <Box
                sx={{
                  display: {
                    xs: 'block',
                    md: 'none'
                  }
                }}
              >
                <GeneratorHeading ref={mobileGeneratorHeadingRef} />
              </Box>
              <CodePreview />
              <ProgressBarMobile />
            </>
          </CSSTransition>

          <Box
            sx={{
              position: 'relative',
              flex: {
                xs: 1,
                md: 1.5,
                lg: 1
              },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              overflow: 'hidden',
              height: '100%'
            }}
          >
            <ScrollableContainer />
            {displayFormActionButtons && <FormActionButtons />}
          </Box>
        </Stack>
      </Stack>
    </Container>
  )
}
